/**
 * Accessibility (a11y) Check Module
 * Detects accessibility issues in React/HTML code
 */

import type { CheckContext, CodeReviewFinding } from '../types.js';
import { generateFindingId } from '../types.js';

// Image accessibility patterns
const IMG_WITHOUT_ALT = /<img(?![^>]*\balt\s*=)[^>]*>/gi;
const IMG_EMPTY_ALT = /<img[^>]*\balt\s*=\s*["']\s*["'][^>]*>/gi;

// Link accessibility patterns
const LINK_WITHOUT_TEXT = /<a[^>]*>\s*<\/a>/gi;
const LINK_ONLY_ICON = /<a[^>]*>\s*<(?:svg|i|span[^>]*class="[^"]*icon)[^>]*>\s*<\/a>/gi;

// Button accessibility patterns
const BUTTON_WITHOUT_TEXT = /<button[^>]*>\s*<(?:svg|i|span[^>]*icon)[^>]*>\s*<\/button>/gi;

// Form accessibility patterns - INPUT_WITHOUT_LABEL pattern reserved for future use

// Interactive element patterns
const ONCLICK_ON_DIV = /<div[^>]*onClick[^>]*>/gi;
const ONCLICK_ON_SPAN = /<span[^>]*onClick[^>]*>/gi;

// Heading patterns
const HEADING_PATTERN = /<h([1-6])/gi;

// Focus patterns
const POSITIVE_TABINDEX = /tabIndex\s*=\s*\{?\s*["']?([1-9]\d*)["']?\s*\}?/gi;
const OUTLINE_NONE = /outline\s*:\s*none|outline\s*:\s*0/gi;

// ARIA patterns
const ARIA_HIDDEN_FOCUSABLE = /aria-hidden\s*=\s*["']true["'][^>]*(?:tabIndex|onClick|onKeyDown)/gi;

export async function checkAccessibility(context: CheckContext): Promise<CodeReviewFinding[]> {
  const findings: CodeReviewFinding[] = [];

  for (const file of context.files) {
    // Only check frontend files
    if (!['tsx', 'jsx', 'typescript', 'javascript'].includes(file.language)) continue;
    if (!isUiFile(file.relativePath)) continue;

    const lines = file.content.split('\n');

    // Check for images without alt
    checkPattern(file, lines, IMG_WITHOUT_ALT, {
      title: 'Image Missing Alt Text',
      description: 'Images must have an alt attribute for screen readers.',
      severity: 'critical',
      suggestion: 'Add alt="description" to the image. Use alt="" only for decorative images.',
      cweId: undefined,
    }, findings);

    // Check for empty alt on non-decorative images
    let match;
    IMG_EMPTY_ALT.lastIndex = 0;
    while ((match = IMG_EMPTY_ALT.exec(file.content)) !== null) {
      // Check if it looks decorative (has aria-hidden or role="presentation")
      if (match[0].includes('aria-hidden') || match[0].includes('role="presentation"')) continue;

      const lineNumber = getLineNumber(file.content, match.index);

      findings.push({
        id: generateFindingId('accessibility'),
        category: 'accessibility',
        severity: 'warning',
        title: 'Empty Alt on Potentially Informative Image',
        description: 'Empty alt text should only be used for decorative images.',
        file: file.relativePath,
        line: lineNumber,
        code: truncateCode(lines[lineNumber - 1]?.trim() || ''),
        suggestion: 'Add descriptive alt text or add role="presentation" if truly decorative.',
        autoFixable: false,
      });
    }

    // Check for links without accessible text
    checkPattern(file, lines, LINK_WITHOUT_TEXT, {
      title: 'Empty Link',
      description: 'Links must have accessible text content.',
      severity: 'critical',
      suggestion: 'Add text content or aria-label to the link.',
    }, findings);

    checkPattern(file, lines, LINK_ONLY_ICON, {
      title: 'Icon-Only Link',
      description: 'Links containing only icons need accessible labels.',
      severity: 'warning',
      suggestion: 'Add aria-label or visually hidden text to describe the link.',
    }, findings);

    // Check for buttons without accessible text
    checkPattern(file, lines, BUTTON_WITHOUT_TEXT, {
      title: 'Icon-Only Button',
      description: 'Buttons containing only icons need accessible labels.',
      severity: 'warning',
      suggestion: 'Add aria-label or visually hidden text to describe the button action.',
    }, findings);

    // Check for click handlers on non-interactive elements
    checkPattern(file, lines, ONCLICK_ON_DIV, {
      title: 'Click Handler on Non-Interactive Element',
      description: 'div elements with onClick are not keyboard accessible by default.',
      severity: 'warning',
      suggestion: 'Use a button element, or add role="button", tabIndex={0}, and onKeyDown handler.',
    }, findings);

    checkPattern(file, lines, ONCLICK_ON_SPAN, {
      title: 'Click Handler on Non-Interactive Element',
      description: 'span elements with onClick are not keyboard accessible by default.',
      severity: 'warning',
      suggestion: 'Use a button element, or add role="button", tabIndex={0}, and onKeyDown handler.',
    }, findings);

    // Check for positive tabIndex
    POSITIVE_TABINDEX.lastIndex = 0;
    while ((match = POSITIVE_TABINDEX.exec(file.content)) !== null) {
      const lineNumber = getLineNumber(file.content, match.index);

      findings.push({
        id: generateFindingId('accessibility'),
        category: 'accessibility',
        severity: 'warning',
        title: 'Positive tabIndex',
        description: `tabIndex="${match[1]}" disrupts natural tab order and confuses keyboard users.`,
        file: file.relativePath,
        line: lineNumber,
        code: truncateCode(lines[lineNumber - 1]?.trim() || ''),
        suggestion: 'Use tabIndex={0} to add to natural tab order, or tabIndex={-1} to remove. Avoid positive values.',
        autoFixable: true,
      });
    }

    // Check for outline:none without alternative focus indicator
    OUTLINE_NONE.lastIndex = 0;
    while ((match = OUTLINE_NONE.exec(file.content)) !== null) {
      const lineNumber = getLineNumber(file.content, match.index);
      const context = file.content.slice(Math.max(0, match.index - 100), match.index + 200);

      // Check if there's an alternative focus style
      if (context.includes(':focus') && (context.includes('box-shadow') || context.includes('border'))) continue;

      findings.push({
        id: generateFindingId('accessibility'),
        category: 'accessibility',
        severity: 'critical',
        title: 'Focus Indicator Removed',
        description: 'Removing outline without an alternative makes it impossible for keyboard users to see focus.',
        file: file.relativePath,
        line: lineNumber,
        code: truncateCode(lines[lineNumber - 1]?.trim() || ''),
        suggestion: 'Provide an alternative focus indicator using box-shadow or border on :focus state.',
        autoFixable: false,
      });
    }

    // Check for aria-hidden on focusable elements
    checkPattern(file, lines, ARIA_HIDDEN_FOCUSABLE, {
      title: 'Focusable Element Hidden from Assistive Technology',
      description: 'Elements with aria-hidden="true" should not be focusable.',
      severity: 'critical',
      suggestion: 'Remove aria-hidden="true" or add tabIndex={-1} and remove click handlers.',
    }, findings);

    // Check heading hierarchy
    checkHeadingHierarchy(file, lines, findings);
  }

  return findings;
}

function checkPattern(
  file: { relativePath: string; content: string },
  lines: string[],
  pattern: RegExp,
  issue: {
    title: string;
    description: string;
    severity: 'blocking' | 'critical' | 'warning' | 'info';
    suggestion: string;
    cweId?: string;
  },
  findings: CodeReviewFinding[]
): void {
  let match;
  pattern.lastIndex = 0;
  while ((match = pattern.exec(file.content)) !== null) {
    const lineNumber = getLineNumber(file.content, match.index);

    findings.push({
      id: generateFindingId('accessibility'),
      category: 'accessibility',
      severity: issue.severity,
      title: issue.title,
      description: issue.description,
      file: file.relativePath,
      line: lineNumber,
      code: truncateCode(lines[lineNumber - 1]?.trim() || ''),
      suggestion: issue.suggestion,
      autoFixable: false,
      cweId: issue.cweId,
    });
  }
}

function checkHeadingHierarchy(
  file: { relativePath: string; content: string },
  lines: string[],
  findings: CodeReviewFinding[]
): void {
  const headings: { level: number; line: number }[] = [];

  let match;
  HEADING_PATTERN.lastIndex = 0;
  while ((match = HEADING_PATTERN.exec(file.content)) !== null) {
    headings.push({
      level: parseInt(match[1], 10),
      line: getLineNumber(file.content, match.index),
    });
  }

  // Check for skipped levels
  for (let i = 1; i < headings.length; i++) {
    const current = headings[i];
    const previous = headings[i - 1];

    // Skipping more than one level (e.g., h1 -> h3)
    if (current.level > previous.level + 1) {
      findings.push({
        id: generateFindingId('accessibility'),
        category: 'accessibility',
        severity: 'warning',
        title: 'Heading Level Skipped',
        description: `Heading jumps from h${previous.level} to h${current.level}. Screen reader users may think content is missing.`,
        file: file.relativePath,
        line: current.line,
        code: truncateCode(lines[current.line - 1]?.trim() || ''),
        suggestion: `Use h${previous.level + 1} instead of h${current.level}.`,
        autoFixable: true,
      });
    }
  }

  // Check if page starts with h1
  if (headings.length > 0 && headings[0].level !== 1) {
    findings.push({
      id: generateFindingId('accessibility'),
      category: 'accessibility',
      severity: 'info',
      title: 'Page Does Not Start with h1',
      description: 'Pages should typically start with an h1 heading.',
      file: file.relativePath,
      line: headings[0].line,
      suggestion: 'Consider adding an h1 as the main page heading.',
      autoFixable: false,
    });
  }
}

function isUiFile(filePath: string): boolean {
  const lower = filePath.toLowerCase();
  return (
    lower.includes('/pages/') ||
    lower.includes('/components/') ||
    lower.includes('/views/') ||
    lower.includes('/ui/') ||
    lower.endsWith('.tsx') ||
    lower.endsWith('.jsx')
  );
}

function getLineNumber(content: string, index: number): number {
  return content.slice(0, index).split('\n').length;
}

function truncateCode(code: string, maxLength: number = 100): string {
  if (code.length <= maxLength) return code;
  return code.slice(0, maxLength - 3) + '...';
}
